// ============================================================
// TMR Portal Admin — o valor de uma regra: o padrão da TMR ou a exceção de uma empresa (25)
// ============================================================
import { useId, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { TextField } from '../../components/fields';
import { Icons } from '../../lib/icons';
import { parseRuleValue, parseScoreBands, scoreBandDrafts } from '../../data/businessRules/form';
import { ruleOptionCopy, ruleRangeLabel, ruleValueLabel } from '../../data/businessRules/status';
import type { RuleValue, RuleValueType } from '../../data/businessRules/contract';
import { ScoreBandsField } from './ScoreBandsField';

/** Props for {@link RuleValueModal}. */
interface RuleValueModalProps {
  /** The dialog's title — "Alterar o padrão", "Definir exceção". */
  title: string;
  /** The rule being set: its key (for the value's reading), name and what it accepts. */
  rule: {
    key: string;
    name: string;
    valueType: RuleValueType;
    minimum: number | null;
    maximum: number | null;
    options: string[];
  };
  /** The line under the title; the rule's name when absent. The rule's page names the Empresa here. */
  lead?: string;
  /** The value the field starts with — the one in force. */
  initialValue: RuleValue;
  /** One sentence on what saving reaches, shown above the buttons. */
  reach: string;
  /**
   * Why saving is not possible for this Empresa — shown as a warning, with the confirming button disabled.
   * The rule's page picks the Empresa inside the dialog's flow, so it can land on one not yet replicated.
   */
  blocked?: string | null;
  /** The label of the confirming button. */
  confirmLabel: string;
  /** Sends the value. The dialog stays open, disabled, until it settles, and shows the server's refusal. */
  onSave: (value: RuleValue) => Promise<unknown>;
  onClose: () => void;
}

/**
 * The value of a rule, for either side — the TMR's default on the global screen, one Empresa's exception on
 * its tab or on the rule's page. They differ only in the sentence that says what saving reaches.
 *
 * An integer rule is typed, with its range in the field's hint; a choice rule is picked from its options,
 * each with what it does; a score-bands rule is edited band by band (`26` §9). Either way the server checks
 * again, and its message is the one shown if it refuses (`25` §5).
 */
export function RuleValueModal({
  title,
  rule,
  lead,
  initialValue,
  reach,
  blocked,
  confirmLabel,
  onSave,
  onClose,
}: RuleValueModalProps) {
  const bandsRule = rule.valueType === 'ScoreBands';
  const [input, setInput] = useState(Array.isArray(initialValue) ? '' : String(initialValue));
  const [bands, setBands] = useState(() => scoreBandDrafts(initialValue));
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  const parsed = bandsRule ? parseScoreBands(bands, rule) : parseRuleValue(input, rule);

  const submit = async () => {
    if (!parsed.ok) {
      setFieldError(parsed.error);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(parsed.value);
      onClose();
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const change = (value: string) => {
    setInput(value);
    setFieldError(null);
  };

  const changeBands = (next: typeof bands) => {
    setBands(next);
    setFieldError(null);
  };

  return (
    <ModalShell
      title={title}
      lead={lead ?? rule.name}
      width={bandsRule ? 560 : 480}
      onClose={saving ? () => undefined : onClose}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {rule.valueType === 'Choice' ? (
          <RuleChoiceField rule={rule} value={input} onChange={change} disabled={saving || Boolean(blocked)} />
        ) : bandsRule ? (
          <>
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{ruleRangeLabel(rule)}</span>
            <ScoreBandsField rule={rule} bands={bands} onChange={changeBands} disabled={saving || Boolean(blocked)} />
            {parsed.ok && (
              <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{ruleValueLabel(rule, parsed.value)}</span>
            )}
          </>
        ) : (
          <>
            <TextField
              label="Valor"
              hint={ruleRangeLabel(rule)}
              value={input}
              inputMode="numeric"
              onChange={change}
              error={fieldError}
              disabled={saving || Boolean(blocked)}
            />
            {parsed.ok && (
              <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{ruleValueLabel(rule, parsed.value)}</span>
            )}
          </>
        )}
        {rule.valueType !== 'Integer' && fieldError && (
          <span style={{ fontSize: 12, color: 'var(--danger)' }}>{fieldError}</span>
        )}

        {blocked ? <Notice tone="warning">{blocked}</Notice> : <Notice tone="neutral">{reach}</Notice>}

        {saveError !== null && (
          <Notice tone="danger">
            {saveError instanceof ApiError ? saveError.detail : 'Não foi possível salvar o valor.'}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Btn>
        <Btn variant="primary" icon={Icons.check} onClick={() => void submit()} disabled={saving || Boolean(blocked)}>
          {saving ? 'Salvando…' : confirmLabel}
        </Btn>
      </div>
    </ModalShell>
  );
}

/** Props for {@link RuleChoiceField}. */
interface RuleChoiceFieldProps {
  rule: { key: string; options: string[] };
  /** The chosen option, as the API sends it. */
  value: string;
  onChange: (option: string) => void;
  disabled: boolean;
}

/**
 * The options of a choice rule as a radio group, each with its name and what choosing it does. The
 * description is the point: "Legado" alone does not say that a clinic already on the Vendas stops seeing its
 * sales there, and that is what should stop an operator from picking it.
 */
function RuleChoiceField({ rule, value, onChange, disabled }: RuleChoiceFieldProps) {
  const id = useId();

  return (
    <div role="radiogroup" aria-label="Valor" style={{ display: 'grid', gap: 8 }}>
      {rule.options.map((option) => {
        const copy = ruleOptionCopy(rule, option);
        const active = option === value;

        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={copy.label}
            aria-describedby={copy.description ? `${id}-${option}` : undefined}
            disabled={disabled}
            onClick={() => onChange(option)}
            style={{
              display: 'grid',
              gap: 3,
              padding: '12px 14px',
              textAlign: 'left',
              cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${active ? 'var(--forest-900)' : 'var(--border-default)'}`,
              boxShadow: active ? 'inset 0 0 0 1px var(--forest-900)' : 'none',
              background: active ? 'var(--sage-100)' : 'var(--cream-50)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-sans)',
              opacity: disabled && !active ? 0.6 : 1,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' }}>{copy.label}</span>
            {copy.description && (
              <span id={`${id}-${option}`} style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
                {copy.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
